/** @jsxImportSource ../i18n */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { WifiOff } from 'lucide-react';
import { fetchSettings } from '../store/slices/authSlice';
import { fetchSaxoStatus } from '../store/slices/portfolioSlice';
import Chatbot from './Chatbot';
import { Banner, Spinner } from './ui';
import { Sidebar } from './shell/Sidebar';
import { TopBar } from './shell/TopBar';
import { MobileDrawer } from './shell/MobileDrawer';
import { CommandPalette } from './shell/CommandPalette';
import { ChatLauncher } from './shell/ChatLauncher';
import { ShellContext } from './shell/ShellContext';
import { routeMeta, universeOf } from './shell/navConfig';
import { useAmbient } from '../services/useAmbient';
import { tr } from '../i18n';

export const SIDEBAR_KEY = 'ui.sidebarCollapsed';

const Layout = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const settings = useSelector((state) => state.auth.settings);
  const { backendAvailable, saxoStatusChecked, saxoStatusLoading } = useSelector((state) => state.portfolio);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === '1');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatContext, setChatContext] = useState('');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const ambient = useAmbient(backendAvailable);

  useEffect(() => {
    dispatch(fetchSaxoStatus());
    dispatch(fetchSettings());
  }, [dispatch]);

  useEffect(() => {
    if (!backendAvailable && saxoStatusChecked) {
      const interval = setInterval(() => {
        dispatch(fetchSaxoStatus());
      }, 10000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [dispatch, backendAvailable, saxoStatusChecked]);

  useEffect(() => {
    setDrawerOpen(false);
    document.title = `${tr(routeMeta(pathname).title)} · PEA Wallet`;
  }, [pathname]);

  // The Trading section switches the whole app (shell included) to its
  // own colours, so real orders never look like the rest of the app.
  const universe = universeOf(pathname);
  useEffect(() => {
    const html = document.documentElement;
    if (universe === 'trading') html.setAttribute('data-universe', 'trading');
    else html.removeAttribute('data-universe');
  }, [universe]);
  useEffect(() => () => document.documentElement.removeAttribute('data-universe'), []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      localStorage.setItem(SIDEBAR_KEY, current ? '0' : '1');
      return !current;
    });
  }, []);
  // openChat(message) pre-fills Gladys with a context message (e.g. an
  // analysis to discuss); event handlers calling it pass no string.
  const openChat = useCallback((message) => {
    setChatContext(typeof message === 'string' ? message : '');
    setChatOpen(true);
  }, []);
  const closeChat = useCallback(() => {
    setChatOpen(false);
    setChatContext('');
  }, []);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const shell = useMemo(() => ({ openChat, openPalette, ambient }), [openChat, openPalette, ambient]);

  return (
    <ShellContext.Provider value={shell}>
      <div className="min-h-screen" style={{ '--sidebar-w': collapsed ? '76px' : '264px' }}>
        <aside className="fixed inset-y-0 left-0 z-sidebar hidden w-[var(--sidebar-w)] border-r border-line bg-surface-1/95 transition-[width] duration-300 ease-standard lg:block">
          <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapsed} />
        </aside>
        <MobileDrawer open={drawerOpen} onClose={closeDrawer} />

        <main className="min-w-0 overflow-x-clip transition-[margin] duration-300 ease-standard lg:ml-[var(--sidebar-w)]">
          <TopBar onOpenMenu={() => setDrawerOpen(true)} onOpenPalette={openPalette} />
          {!backendAvailable && saxoStatusChecked && (
            <div className="mx-auto max-w-[1440px] px-4 pt-4 sm:px-6 lg:px-10">
              <Banner tone="loss" icon={WifiOff} role="status" action={saxoStatusLoading ? <Spinner size={14} /> : null}>
                Serveur injoignable — nouvelle tentative automatique toutes les 10 secondes.
              </Banner>
            </div>
          )}
          <div className="mx-auto w-full max-w-[1440px] px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
            <Outlet />
          </div>
        </main>

        <ChatLauncher onOpen={openChat} ambient={ambient} mascotVariant={settings?.mascot_variant} />
        <CommandPalette open={paletteOpen} onClose={closePalette} onOpenChat={openChat} />
        <Chatbot isOpen={chatOpen} onClose={closeChat} initialMessage={chatContext} mascotVariant={settings?.mascot_variant} ambient={ambient} />
      </div>
    </ShellContext.Provider>
  );
};

export default Layout;
